import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MID, TEXT_MUTED } from '../styles/shared';
import { trackEvent } from '../utils/analytics';

const CHECK = '\u2713';

const featureMessages = {
  'ai-tools': 'Unlock unlimited AI tools',
  'journal-entries': 'Create unlimited journal entries',
  'pdf-reports': 'Generate unlimited PDF reports',
  'auto-tagging': 'Enable auto-tagging on every entry',
  default: 'Unlock all Pro features',
};

export default function UpgradeModal({ isOpen, onClose, feature }) {
  const [loading, setLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [error, setError] = useState('');

  const subtitle = featureMessages[feature] || featureMessages.default;

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const fetchCheckoutUrl = async () => {
    const res = await apiClient.post('/api/subscription/create-checkout', {});
    return res.data?.data?.checkoutUrl || res.data?.data?.url || '';
  };

  const handleUpgrade = async () => {
    // clicked_upgrade: fires on the CTA click, regardless of whether the
    // subsequent /create-checkout request succeeds. `feature` is whatever
    // the caller passed in (e.g. 'ai-tools', 'journal-entries').
    trackEvent('clicked_upgrade', { feature: feature || 'default' });
    setLoading(true);
    setError('');
    try {
      const url = await fetchCheckoutUrl();
      if (url) {
        setCheckoutUrl(url);
        // Mirror PricingPage: fire started_checkout right before the redirect.
        trackEvent('started_checkout', { plan: 'pro_monthly' });
        window.location.href = url;
      } else {
        setError('Could not create checkout session. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      let url = checkoutUrl;
      if (!url) {
        setLoading(true);
        url = await fetchCheckoutUrl();
        setCheckoutUrl(url);
        setLoading(false);
      }
      if (url) {
        await navigator.clipboard.writeText(url);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2500);
      }
    } catch {
      setError('Could not copy link. Please try the Upgrade button instead.');
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '440px',
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          style={{
            position: 'absolute', top: '12px', right: '12px',
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '1.25rem', color: TEXT_MUTED, padding: '0.25rem',
          }}
        >
          x
        </button>

        <h2 id="upgrade-modal-title" style={{
          fontSize: '1.25rem', fontWeight: '700', color: TEXT_DARK, marginBottom: '0.35rem',
        }}>
          Upgrade to BragSheet Pro
        </h2>
        <p style={{ fontSize: '0.9rem', color: TEXT_MID, marginBottom: '1.5rem' }}>
          {subtitle}
        </p>

        {/* Price */}
        <div style={{ marginBottom: '1.5rem' }}>
          <span style={{ fontSize: '2rem', fontWeight: '800', color: TEXT_DARK }}>
            $4.99
          </span>
          <span style={{ fontSize: '0.9rem', color: TEXT_MUTED, marginLeft: '0.35rem' }}>
            / month
          </span>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444',
            borderRadius: '8px', padding: '0.6rem 0.875rem', fontSize: '0.85rem', marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        {/* Upgrade button */}
        <button
          onClick={handleUpgrade}
          disabled={loading}
          style={{
            width: '100%', padding: '0.75rem',
            borderRadius: '8px', border: 'none',
            fontSize: '0.95rem', fontWeight: '700',
            backgroundColor: PRIMARY, color: '#ffffff',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'opacity 0.15s',
            marginBottom: '1rem',
          }}
        >
          {loading ? 'Loading...' : 'Upgrade Now'}
        </button>

        {/* Parent section */}
        <div style={{
          backgroundColor: '#f8fafc',
          borderRadius: '10px',
          padding: '0.875rem',
          marginBottom: '1rem',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '0.8rem', color: TEXT_MID, marginBottom: '0.5rem' }}>
            Need a parent to help?
          </p>
          <button
            onClick={handleCopyLink}
            disabled={loading}
            style={{
              padding: '0.45rem 1rem', borderRadius: '6px',
              fontSize: '0.8rem', fontWeight: '600',
              backgroundColor: '#ffffff', color: PRIMARY,
              border: `1px solid ${PRIMARY}`,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.15s',
            }}
          >
            {copySuccess ? `${CHECK} Link Copied!` : 'Copy payment link'}
          </button>
        </div>

        {/* Dismiss */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.85rem', color: TEXT_MUTED,
              textDecoration: 'underline',
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
